import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import type {
  BedrockCatalogModel,
  BedrockModelRequest,
} from "../OrgLlmProviderRecord";

import { orgQueryKeys } from "./orgQueryKeys";

/**
 * Refetch what a catalog change can alter: the catalog itself, the role
 * bindings (whose pickers list verified entries), every org's
 * provider templates (whose Bedrock models come from the catalog), and the
 * admin model-configuration searches (labels and defaults in their rows).
 */
function invalidateCatalogCaches(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: orgQueryKeys.bedrockCatalog(),
  });
  void queryClient.invalidateQueries({
    queryKey: orgQueryKeys.bedrockRoles(),
  });
  void queryClient.invalidateQueries({
    queryKey: orgQueryKeys.modelConfigSearches(),
  });
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey.includes("llmProviderTemplates"),
  });
}

/** Add a model to the Bedrock catalog (Roboto admins). */
export function useCreateBedrockModel() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<BedrockCatalogModel, Error, BedrockModelRequest>({
    mutationFn: (request) => orgService.createBedrockModel(request),
    onSuccess: () => invalidateCatalogCaches(queryClient),
  });
}

/** Replace a catalog model's fields (Roboto admins). */
export function useUpdateBedrockModel() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    BedrockCatalogModel,
    Error,
    { modelId: string; request: BedrockModelRequest }
  >({
    mutationFn: ({ modelId, request }) =>
      orgService.updateBedrockModel(modelId, request),
    onSuccess: () => invalidateCatalogCaches(queryClient),
  });
}

/**
 * Verify a catalog model with a one-token Bedrock call (Roboto admins).
 * Resolves with the model either way; a failure shows as `verified: false`
 * with `last_error`.
 */
export function useVerifyBedrockModel() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<BedrockCatalogModel, Error, { modelId: string }>({
    mutationFn: ({ modelId }) => orgService.verifyBedrockModel(modelId),
    onSuccess: () => invalidateCatalogCaches(queryClient),
  });
}

/** Remove a catalog model (Roboto admins). */
export function useDeleteBedrockModel() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { modelId: string }>({
    mutationFn: ({ modelId }) => orgService.deleteBedrockModel(modelId),
    onSuccess: () => invalidateCatalogCaches(queryClient),
  });
}
